import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';

interface Holiday {
    id: number;
    date: string;
    description: string;
    country: string;
    is_active: boolean;
}

const AdminHolidaysScreen: React.FC = () => {
    const { token, hasPermission } = useAuth();
    const [holidays, setHolidays] = useState<Holiday[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingHoliday, setEditingHoliday] = useState<Partial<Holiday> | null>(null);

    const fetchHolidays = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/v1/admin/holidays`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                const data = await response.json();
                setHolidays(data);
            }
        } catch (error) {
            console.error('Error fetching holidays:', error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (token && hasPermission('admin_platform')) {
            fetchHolidays();
        }
    }, [token, hasPermission, fetchHolidays]);

    const handleAdd = () => {
        setEditingHoliday({
            date: new Date().toISOString().split('T')[0],
            description: '',
            country: "Côte d'Ivoire",
            is_active: true
        });
        setIsModalOpen(true);
    };

    const handleEdit = (holiday: Holiday) => {
        setEditingHoliday(holiday);
        setIsModalOpen(true);
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Voulez-vous vraiment supprimer ce jour férié ?')) return;
        try {
            const response = await fetch(`${API_URL}/v1/admin/holidays/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                fetchHolidays();
            }
        } catch (error) {
            console.error('Error deleting holiday:', error);
        }
    };

    const handleSave = async () => {
        if (!editingHoliday || !editingHoliday.date) return;

        // Clean object for API
        const payload = {
            date: editingHoliday.date,
            description: editingHoliday.description || '',
            country: editingHoliday.country || "Côte d'Ivoire",
            is_active: editingHoliday.is_active !== undefined ? editingHoliday.is_active : true
        };

        const method = editingHoliday.id ? 'PUT' : 'POST';
        const url = editingHoliday.id 
            ? `${API_URL}/v1/admin/holidays/${editingHoliday.id}` 
            : `${API_URL}/v1/admin/holidays`;

        try {
            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                fetchHolidays();
                setIsModalOpen(false);
                setEditingHoliday(null);
            } else {
                const err = await response.json();
                alert(`Erreur: ${err.detail || 'Opération échouée'}`);
            }
        } catch (error) {
            console.error('Error saving holiday:', error);
        }
    };

    if (!hasPermission('admin_platform')) {
        return (
            <SiteLayout>
                <Box><p style={{ padding: '20px' }}>Accès refusé. Permission admin_platform requise.</p></Box>
            </SiteLayout>
        )
    }

    return (
        <SiteLayout>
            <Header icon='event_busy' title='Calendrier Boursier BRVM' />
            <Box>
                <div className='box-title box-vertical-padding box-horizontal-padding' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <p>Jours Fériés & Fermetures de la Bourse</p>
                    <button className='button button-purple' onClick={handleAdd}>
                        <i className='material-icons' style={{ fontSize: '18px', marginRight: '5px' }}>add</i>
                        Ajouter un jour
                    </button>
                </div>
                <div className='box-content box-horizontal-padding' style={{ padding: '20px' }}>
                    {loading ? (
                        <p>Chargement du calendrier...</p>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className='data-table' style={{ width: '100%' }}>
                                <thead>
                                    <tr style={{ background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)' }}>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left' }}>Date</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left' }}>Description</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left' }}>Pays</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'center' }}>Statut</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'right' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {holidays.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                                                📅 Aucun jour férié configuré pour le moment.
                                            </td>
                                        </tr>
                                    ) : holidays.map((h, index) => (
                                        <tr key={h.id} style={{ backgroundColor: index % 2 === 0 ? 'transparent' : 'var(--bg-hover)' }}>
                                            <td style={{ padding: '12px 16px', fontWeight: 600 }}>{h.date}</td>
                                            <td style={{ padding: '12px 16px' }}>{h.description}</td>
                                            <td style={{ padding: '12px 16px' }}>{h.country}</td>
                                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                                <span style={{ 
                                                    padding: '4px 10px', 
                                                    borderRadius: '20px', 
                                                    fontSize: '11px',
                                                    fontWeight: 600,
                                                    backgroundColor: h.is_active ? '#def7ec' : '#fde2e2',
                                                    color: h.is_active ? '#03543f' : '#9b1c1c',
                                                    border: h.is_active ? '1px solid #31c48d' : '1px solid #f8b4b4'
                                                }}>
                                                    {h.is_active ? 'ACTIF' : 'INACTIF'}
                                                </span>
                                            </td>
                                            <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                                                <button onClick={() => handleEdit(h)} className='btn-icon' style={{ marginRight: '10px', background: 'none', border: 'none', cursor: 'pointer' }}>
                                                    <i className='material-icons' style={{ color: '#667eea', fontSize: '20px' }}>edit</i>
                                                </button>
                                                <button onClick={() => handleDelete(h.id)} className='btn-icon' style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                                                    <i className='material-icons' style={{ color: '#ef4444', fontSize: '20px' }}>delete</i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </Box>

            {isModalOpen && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}>
                    <div style={{ background: 'var(--bg-box)', padding: '30px', borderRadius: '16px', width: '480px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
                        <h3 style={{ marginBottom: '25px', fontSize: '1.25rem', fontWeight: 700 }}>
                            {editingHoliday?.id ? 'Modifier le jour férié' : 'Ajouter un nouveau jour férié'}
                        </h3>
                        
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: 'var(--text-heading)' }}>Date</label>
                            <input
                                type='date'
                                value={editingHoliday?.date || ''}
                                onChange={(e) => setEditingHoliday({ ...editingHoliday, date: e.target.value })}
                                style={{ width: '100%', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '8px', backgroundColor: 'var(--bg-input)', color: 'var(--text-main)' }}
                            />
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: 'var(--text-heading)' }}>Description</label>
                            <input
                                type='text'
                                placeholder='ex: Lundi de Pâques'
                                value={editingHoliday?.description || ''}
                                onChange={(e) => setEditingHoliday({ ...editingHoliday, description: e.target.value })}
                                style={{ width: '100%', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '8px', backgroundColor: 'var(--bg-input)', color: 'var(--text-main)' }}
                            />
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: 'var(--text-heading)' }}>Pays</label>
                            <input
                                type='text'
                                value={editingHoliday?.country || ''}
                                onChange={(e) => setEditingHoliday({ ...editingHoliday, country: e.target.value })}
                                style={{ width: '100%', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '8px', backgroundColor: 'var(--bg-input)', color: 'var(--text-main)' }}
                            />
                        </div>

                        <div style={{ marginBottom: '25px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <input
                                type='checkbox'
                                id='is_active'
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                checked={editingHoliday?.is_active || false}
                                onChange={(e) => setEditingHoliday({ ...editingHoliday, is_active: e.target.checked })}
                            />
                            <label htmlFor='is_active' style={{ fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>Le marché est fermé ce jour-là</label>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '15px' }}>
                            <button onClick={() => setIsModalOpen(false)} style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'transparent', cursor: 'pointer', fontWeight: 600 }}>
                                Annuler
                            </button>
                            <button onClick={handleSave} className='button button-purple' style={{ padding: '10px 25px' }}>
                                Enregistrer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </SiteLayout>
    );
};

export default AdminHolidaysScreen;
